import { preference, preferenceKeys, setPreference, PREFERENCES_CHANGE_EVENT } from './person-preferences.js';

/**
 * CAOS-751 — whether the signed-in person wants a given edge-anchored tab in their right-edge
 * gutter at all.
 *
 * CAOS-450 put every anchored source — the assistant, Report a Bug, the Guidance Center, anything
 * else a package anchors — into one permanent stack, reachable from every route. That is the point
 * of the gutter, and it is also why a person who never uses one of them has no way to stop seeing
 * it: the stack is not route-scoped, so there is nowhere to navigate away from it.
 *
 * This is the answer to that, and it is deliberately the SMALLER of the two available answers.
 *
 * ── WHY THIS LIVES HERE, AND NOT IN THE KERNEL ────────────────────────────────────────────────
 * The ticket named a blocker: a new kernel capability seam letting a package inject its own
 * controls into My Profile. That seam would be worth having, and it is not what showing or hiding
 * an edge tab requires. The host already resolves the full list of anchored sources every render
 * (`plan.injections['edge-anchored']`), and a person's preferences already persist against the
 * PERSON rather than the browser (`person-preferences.ts`, CAOS-470). Both halves exist, so this
 * needs no new contract between the kernel and a package — only a key per source.
 *
 * ── VISIBILITY IS NOT ORDER, AND A PERSON IS NOT AN ORG ───────────────────────────────────────
 * An admin's arrangement of this seam is stored elsewhere on purpose: `caos.seam_order_override`
 * (CAOS-732/736) is ORG-wide, set in Setup, and says what ORDER the tabs stack in. This is one
 * person saying they do not want to look at one of them. Folding the two into one store would make
 * a person's choice look like an admin's, and the first attempt to reconcile them would have to
 * guess which one a value came from.
 *
 * Because it rides `person-preferences`, an org CAN still ship a default (the kernel resolves org
 * defaults beneath a person's own value), and a person who clears their choice falls back to it.
 * That is inherited for free and is not special-cased here.
 *
 * ── THE DEFAULT IS SHOWN ──────────────────────────────────────────────────────────────────────
 * `preference(key) !== false` — the same shape `rail.pinned.<source>` uses. A package that has
 * just been installed appears; nothing chosen means visible. The alternative (absent ⇒ hidden)
 * would make every newly installed package invisible until someone went looking for a checkbox to
 * turn it on, which is indistinguishable from the package being broken.
 */

/**
 * `rail.shown.<sourceKey>`. The `rail.` family is the right-edge gutter's own, alongside
 * `rail.pinned.<sourceKey>` (CAOS-327/470 — whether a visible tab sits proud of the edge or tucks
 * away to a sliver). Pinned and shown are independent: tucking a tab away is a person keeping it
 * and wanting it smaller, hiding it is a person not wanting it.
 */
export const RAIL_SHOWN_PREFIX = 'rail.shown.';

export function railShownKey(sourceKey: string): string {
  return `${RAIL_SHOWN_PREFIX}${sourceKey}`;
}

/** Does this person want this tab in their gutter? Nothing chosen ⇒ yes. */
export function edgeTabShown(sourceKey: string): boolean {
  return preference(railShownKey(sourceKey)) !== false;
}

/**
 * Remember whether this person wants this tab.
 *
 * Writing `true` rather than clearing the key is deliberate: clearing would fall back to whatever
 * the org defaults to, so a person who turned a tab back ON after an org default of hidden would
 * silently lose that the next time preferences resolved. An explicit choice is stored as one.
 */
export function setEdgeTabShown(sourceKey: string, shown: boolean): Promise<boolean> {
  return setPreference(railShownKey(sourceKey), shown);
}

/**
 * Call `onChange(sourceKey)` when this person's visibility choice for a tab changes — including a
 * choice they made on another device, which arrives when their preferences load or reload.
 *
 * Returns the listener so a caller that keeps only the NEWEST subscription can remove the previous
 * one; the rail does exactly that, because a re-rendered frame must not leave an unmounted one
 * subscribed beside the live frame.
 */
export function onEdgeTabVisibilityChange(onChange: (sourceKey: string) => void): (ev: Event) => void {
  const listener = (ev: Event): void => {
    for (const key of (ev as CustomEvent<{ keys?: string[] }>).detail?.keys ?? []) {
      if (key.startsWith(RAIL_SHOWN_PREFIX)) onChange(key.slice(RAIL_SHOWN_PREFIX.length));
    }
  };
  document.addEventListener(PREFERENCES_CHANGE_EVENT, listener);
  return listener;
}

/**
 * Forget every visibility choice this person has made about the gutter, so each tab falls back to
 * what it inherits (the org's default, else shown).
 *
 * This exists because "Reset personalization" promises to return the person's own view to its
 * defaults. A reset that left tabs hidden would leave the one part of the workspace a person cannot
 * navigate away from still altered, with the control that did it now saying everything is back to
 * normal. `null` clears rather than writes `true`: a reset returns to the default, it does not make
 * a new choice on the person's behalf.
 */
export function clearEdgeTabVisibility(): void {
  for (const key of preferenceKeys(RAIL_SHOWN_PREFIX)) void setPreference(key, null);
}
